import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ModalPauseCreateConfirm from './ModalPauseCreateConfirm';
import { getLatestPause, resumePause } from '@/features/membershipPause/api';
import { resolvePauseError } from '@/features/membershipPause/errors';
import { markPauseUnsupportedIfLegacy, useMembershipPauseSupport } from '@/features/membershipPause/hooks';
import { getOpenPause, PauseLike } from '@/features/membershipPause/status';
import { makePauseRequestKey } from '@/features/membershipPause/requestKey';
import {
    buildResumedThenFailedMessage,
    findPauseGateHits,
    formatHitNames,
    getPassKey,
    PauseGateHit,
    PauseGateTarget,
} from '@/features/membershipPause/createGate';

type AlertFn = (title: string, content: string, isError: boolean, ...rest: any[]) => void;

/**
 * 수업 생성 관문 — 7개 생성 경로가 같은 훅을 쓴다(판정은 createGate.ts 한 벌).
 *
 * 사용법:
 *   const pauseGate = useMembershipPauseCreateGate({ createAlert });
 *   if (!(await pauseGate.confirmBeforeCreate(targets))) return;   // 취소·해제 실패면 false
 *   ...기존 생성 그대로...
 *   실패 안내: createAlert(title, pauseGate.decorateFailureMessage(msg), true)
 *   JSX: {pauseGate.modal}
 *
 * 「풀고 등록」: 걸린 수강권을 순서대로 모두 해제한 뒤에만 true. 하나라도 실패하면 생성하지 않는다
 * (앞서 풀린 것은 그대로 두고 안내만 — 다시 정지하지 않는다).
 * 해제 요청 키는 정지(pauseId)마다 이 화면이 살아 있는 동안 하나다. 해제 성공·생성 실패 뒤
 * 같은 정지를 다시 해제해도 서버가 처음 결과를 돌려준다(이미 닫힌 정지도 같은 결과, 설계서 4-2 ②).
 *
 * 생성 화면의 회원·수강권 목록은 여기서 무효화하지 않는다 — 고른 회원·수강권 순서가 바뀌면
 * 인덱스로 고른 값이 어긋날 수 있다. 목록이 낡아 창이 다시 떠도 해제는 같은 키로 재생된다.
 */
export const useMembershipPauseCreateGate = ({ createAlert }: { createAlert: AlertFn }) => {
    const queryClient = useQueryClient();
    const { isSupported } = useMembershipPauseSupport();
    const [hits, setHits] = useState<PauseGateHit[] | null>(null);
    const [isResuming, setIsResuming] = useState(false);
    /**
     * 확인 시도 하나 = 번호 하나. resolver·해제 작업은 이 번호에 묶인다.
     * 취소·새 시도·화면 이탈로 번호가 바뀌면, 그 뒤에 끝난 해제 작업은 아무것도 승인하지 못한다
     * (교차검증 1: A 취소 → B 열기 → A 응답이 B 를 승인하던 문제).
     */
    const attemptRef = useRef<{ id: number; resolve: (proceed: boolean) => void; resuming: boolean } | null>(null);
    const attemptSeqRef = useRef(0);
    const keysRef = useRef(new Map<string, string>());
    const resumedRef = useRef<PauseGateHit[]>([]);
    /**
     * 이 화면에서 알게 된 수강권별 최신 정지(해제 응답·충돌 뒤 다시 읽은 값).
     * 생성 화면의 회원 목록을 다시 불러오지 않고 판정만 최신으로 맞춘다 — 목록을 다시 불러오면
     * 고른 수강권(인덱스)과 직원이 정한 종료 시간이 바뀐다(교차검증 12).
     */
    const latestPauseRef = useRef(new Map<string, PauseLike>());
    /**
     * 기억 갱신 — 늦게 도착한 옛 응답이 최신을 덮지 않게 revision 이 기억한 값보다 작으면 버린다(교차검증 15).
     * 같은 revision 은 덮어쓴다. 기억한 값이 있을 때 revision 없는 응답은 덮어쓰지 않는다.
     */
    /** 관문이 기억한 그 수강권의 열린 정지가 이번 시도가 해제한 정지와 다른가 */
    const rememberedOtherOpenPause = (hit: PauseGateHit) => {
        const open = getOpenPause(latestPauseRef.current.get(hit.key));
        return !!open && open.openId !== hit.open.openId;
    };
    const rememberPause = (key: string, pause: PauseLike) => {
        const existing = latestPauseRef.current.get(key) as { revision?: unknown } | undefined;
        const incoming = (pause as { revision?: unknown } | null | undefined)?.revision;
        if (existing) {
            if (typeof incoming !== 'number') return;
            if (typeof existing.revision === 'number' && incoming < existing.revision) return;
        }
        latestPauseRef.current.set(key, pause);
    };

    const isCurrent = (id: number) => attemptRef.current?.id === id;

    /** 그 시도가 아직 살아 있을 때만 끝낸다. 이미 끝났거나 교체된 시도면 아무것도 하지 않는다. */
    const finish = useCallback((id: number, proceed: boolean) => {
        const attempt = attemptRef.current;
        if (!attempt || attempt.id !== id) return;
        attemptRef.current = null;
        setHits(null);
        setIsResuming(false);
        attempt.resolve(proceed);
    }, []);

    // 화면을 떠나면 열린 확인은 false 로 끝내고 번호를 무효로 만든다.
    useEffect(
        () => () => {
            const attempt = attemptRef.current;
            attemptRef.current = null;
            attempt?.resolve(false);
        },
        [],
    );

    const confirmBeforeCreate = useCallback(
        (targets: PauseGateTarget[]): Promise<boolean> => {
            resumedRef.current = [];
            if (!isSupported) return Promise.resolve(true);
            const found = findPauseGateHits(
                targets.map((target) => {
                    const key = getPassKey(target.ref);
                    return latestPauseRef.current.has(key) ? { ...target, pause: latestPauseRef.current.get(key) } : target;
                }),
            );
            if (found.length === 0) return Promise.resolve(true);
            // 앞 시도가 열려 있으면 취소로 닫고 새로 연다(이중 클릭). 앞 시도의 늦은 응답은 무효.
            const previous = attemptRef.current;
            attemptRef.current = null;
            previous?.resolve(false);
            return new Promise<boolean>((resolve) => {
                attemptSeqRef.current += 1;
                attemptRef.current = { id: attemptSeqRef.current, resolve, resuming: false };
                setIsResuming(false);
                setHits(found);
            });
        },
        [isSupported],
    );

    const refreshPassViews = () =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['membershipChangeLog'] }),
        ]);

    const onResume = async () => {
        const attempt = attemptRef.current;
        // 연타 방지는 상태(isResuming)가 아니라 시도 표식으로 한다 — 다시 그리기 전 두 번째 클릭도 막힌다.
        if (!hits || !attempt || attempt.resuming) return;
        attempt.resuming = true;
        const id = attempt.id;
        setIsResuming(true);
        const done: PauseGateHit[] = [];
        /** 「다른 정지가 열려 있어요」로 멈춘다 — 생성하지 않고, 새 정지는 풀지 않으며, 다시 누르면 기억한 정지로 묻는다. */
        const stopForOtherPause = (hit: PauseGateHit) => {
            refreshPassViews();
            if (!isCurrent(id)) return;
            finish(id, false);
            createAlert(
                '다른 정지가 있어요',
                `${formatHitNames([hit])} 수강권에 다른 정지가 새로 등록돼 있어 수업을 등록하지 않았어요.\n정지 정보를 다시 불러왔어요. 등록을 다시 눌러 확인해 주세요.${
                    done.length ? `\n\n먼저 해제된 수강권: ${formatHitNames(done)}` : ''
                }`,
                true,
            );
        };
        for (const hit of hits) {
            // 다음 해제를 보내기 전에 시도가 아직 유효한지 본다 — 취소·이탈 뒤 남은 해제를 보내지 않는다(검증 13).
            if (!isCurrent(id)) {
                if (done.length) refreshPassViews();
                return;
            }
            const pauseId = hit.open.openId;
            let requestKey = keysRef.current.get(pauseId);
            if (!requestKey) {
                requestKey = makePauseRequestKey();
                keysRef.current.set(pauseId, requestKey);
            }
            try {
                const res = await resumePause(hit.ref, pauseId, requestKey);
                // 성공 표시가 없는 응답(본문을 못 읽은 2xx 등)은 결과 불명 — 같은 키를 지키고 생성하지 않는다(검증 9).
                if (!res?.success) {
                    if (done.length) refreshPassViews();
                    if (!isCurrent(id)) return;
                    finish(id, false);
                    createAlert(
                        '결과 확인 실패',
                        `${formatHitNames([hit])} 정지 해제 결과를 확인하지 못했어요. 수업은 등록하지 않았어요.\n다시 누르면 같은 요청으로 처리돼요.${
                            done.length ? `\n\n먼저 해제된 수강권: ${formatHitNames(done)}` : ''
                        }`,
                        true,
                    );
                    return;
                }
                // 응답의 현재 정지가 아직 열려 있으면(다른 정지가 새로 등록됨) 이 정지는 이미 끝난 것을 재생한 것이다.
                // 새 정지는 자동으로 풀지 않는다 — 생성하지 않고 정지 정보를 다시 불러와 다시 묻게 한다(검증 2).
                if (res.pause?.openId) {
                    rememberPause(hit.key, res.pause);
                    stopForOtherPause(hit);
                    return;
                }
                rememberPause(hit.key, res.pause ?? { openId: null });
                // 응답이 늦게 도착하는 사이 관문이 이미 다른 열린 정지를 기억했으면(상세 조회 등) 승인하지 않는다(검증 16).
                if (rememberedOtherOpenPause(hit)) {
                    stopForOtherPause(hit);
                    return;
                }
                done.push(hit);
            } catch (error) {
                const resolved = resolvePauseError(error);
                if (resolved.dropKey) keysRef.current.delete(pauseId);
                markPauseUnsupportedIfLegacy(queryClient, error);
                // 다른 곳에서 정지가 바뀐 실패(NOT_OPEN·CONFLICT 등)면 정지 정보를 다시 불러온다 — 생성은 이미 멈췄다.
                if (resolved.refetch) {
                    // 그 수강권의 정지만 다시 읽어 다음 판정에 쓴다(회원 목록은 다시 불러오지 않는다).
                    Promise.resolve()
                        .then(() => getLatestPause(hit.ref))
                        .then((latest) => rememberPause(hit.key, latest))
                        .catch(() => undefined);
                    refreshPassViews();
                } else if (done.length) refreshPassViews();
                // 취소·교체·화면 이탈 뒤 끝난 작업이면 안내도 하지 않는다(그 시도는 이미 false 로 끝났다).
                if (!isCurrent(id)) return;
                finish(id, false);
                const failedNames = formatHitNames([hit]);
                const doneNote = done.length ? `\n\n먼저 해제된 수강권: ${formatHitNames(done)}` : '';
                createAlert(
                    '정지 해제 실패',
                    `${failedNames ? `${failedNames} ` : ''}정지 해제에 실패해 수업을 등록하지 않았어요.\n${resolved.message}${doneNote}`,
                    true,
                );
                return;
            }
        }
        // 마지막 승인 직전에도 기억한 정지를 다시 본다(검증 16).
        const otherOpen = done.find((hit) => rememberedOtherOpenPause(hit));
        if (otherOpen) {
            stopForOtherPause(otherOpen);
            return;
        }
        refreshPassViews();
        if (!isCurrent(id)) return;
        resumedRef.current = done;
        finish(id, true);
    };

    /** 「풀고 등록」 뒤 생성이 실패했으면 기존 실패 문구 앞에 "정지는 해제됐어요" 를 붙인다. */
    const decorateFailureMessage = useCallback((message?: string) => {
        if (resumedRef.current.length === 0) return String(message ?? '');
        return buildResumedThenFailedMessage(formatHitNames(resumedRef.current), message);
    }, []);

    const modal = hits ? (
        <ModalPauseCreateConfirm
            hits={hits}
            isResuming={isResuming}
            onResume={onResume}
            onKeep={() => attemptRef.current && finish(attemptRef.current.id, true)}
            onCancel={() => attemptRef.current && finish(attemptRef.current.id, false)}
        />
    ) : null;

    return { confirmBeforeCreate, decorateFailureMessage, modal, isSupported };
};

export default useMembershipPauseCreateGate;
